import { Box, Chip, Stack, Typography } from '@mui/material';
import { useLookups } from '../api/lookups';
import { getUiLanguage, tr } from '../lib/i18n';

type Ref = { id: number; code: string; name_ar: string };

/**
 * The features of a property / request by group (التكييف والتدفئة، الأثاث …), each feature with its chosen
 * details: «أجهزة كهربائية: ثلاجة، غسالة». Falls back to plain chips while the lookups load.
 */
export function FeatureChips({ features }: { features: Ref[] }) {
  const lookups = useLookups();
  const en = getUiLanguage() === 'en';
  const all = lookups.data?.features ?? [];
  const meta = new Map(all.map((f) => [f.id, f]));
  const name = (f: Ref) => {
    const m = meta.get(f.id);
    return en && m?.name_en?.trim() ? m.name_en : f.name_ar;
  };
  const ids = new Set(features.map((f) => f.id));
  const parentOf = (f: Ref) => meta.get(f.id)?.parent_id ?? null;
  // a detail whose feature is not saved stands on its own
  const tops = features.filter((f) => !parentOf(f) || !ids.has(parentOf(f)!));
  const label = (f: Ref) => {
    const kids = features.filter((k) => parentOf(k) === f.id).map(name);
    return kids.length ? `${name(f)}: ${kids.join(en ? ', ' : '، ')}` : name(f);
  };
  const chips = (list: Ref[]) => (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {list.map((f) => (
        <Chip key={f.id} size="small" label={label(f)} sx={{ height: 'auto', minHeight: 24, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }} />
      ))}
    </Stack>
  );

  const groups = lookups.data?.featureGroups ?? [];
  if (!groups.length) return chips(tops);
  const known = new Set(groups.map((g) => g.id));
  const sections = [
    ...groups.map((g) => ({ key: g.id, title: en && g.name_en?.trim() ? g.name_en : g.name_ar, items: tops.filter((f) => meta.get(f.id)?.group_id === g.id) })),
    { key: 0, title: tr('أخرى', 'Other'), items: tops.filter((f) => { const g = meta.get(f.id)?.group_id; return !g || !known.has(g); }) },
  ].filter((s) => s.items.length);
  return (
    <Stack spacing={1.25}>
      {sections.map((s) => (
        <Box key={s.key}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{s.title}</Typography>
          {chips(s.items)}
        </Box>
      ))}
    </Stack>
  );
}
